import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth";
import { api } from "../api";
import { User, Gauge, Languages, LogOut, Smartphone, Info, Link2, Send, CheckCircle2, AlertTriangle, DownloadCloud, Trash2 } from "lucide-react";
import EmergencyContacts from "../components/EmergencyContacts";

export default function Settings() {
  const { user, setUser, t, language, changeLanguage, logout } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState(user?.name || "");
  const [speed, setSpeed] = useState(user?.speed_limit || 80);
  const [chatId, setChatId] = useState(user?.telegram_chat_id || "");
  const [savedMsg, setSavedMsg] = useState("");
  const [tgResult, setTgResult] = useState(null);
  const [tgErr, setTgErr] = useState("");
  const [tgSending, setTgSending] = useState(false);

  const saveProfile = async () => {
    const { data } = await api.patch("/auth/me", {
      name,
      speed_limit: Number(speed),
      telegram_chat_id: chatId || null,
    });
    setUser(data);
    setSavedMsg(t.saved);
    setTimeout(() => setSavedMsg(""), 1800);
  };

  const sendTelegramTest = async () => {
    setTgResult(null); setTgErr(""); setTgSending(true);
    try {
      const { data } = await api.post("/telegram/test", { chat_id: chatId || undefined });
      setTgResult(data);
    } catch (e) {
      setTgErr(e?.response?.data?.detail || e.message || "Failed");
    } finally { setTgSending(false); }
  };

  const onLogout = () => { logout(); nav("/login", { replace: true }); };

  const onDeleteAccount = async () => {
    if (!window.confirm("Permanently delete your account? All devices, trips, alerts, and settings will be erased. This cannot be undone.")) return;
    setTgSending(true);
    try {
      await api.delete("/auth/me");
      logout();
      nav("/login", { replace: true });
    } catch (e) {
      alert("Failed to delete account: " + (e?.response?.data?.detail || e.message));
      setTgSending(false);
    }
  };

  const inputCls = "w-full border border-surface-border rounded-xl px-4 py-3 text-[14px] outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/10 bg-surface-soft transition-all font-body text-ink placeholder:text-ink-subtle";

  return (
    <div data-testid="settings-screen" className="min-h-[100dvh] bg-surface-soft pb-28">

      {/* Sticky frosted header */}
      <div className="screen-header px-5 pt-6 pb-4">
        <h1 className="text-[22px] font-heading font-black text-ink">{t.settings}</h1>
        <p className="text-[12px] text-ink-muted mt-0.5 font-body">{user?.email}</p>
      </div>

      <div className="px-4 mt-4 space-y-3">

        {/* Profile card */}
        <div className="bg-white border border-surface-border rounded-2xl p-5 space-y-4">
          <SectionHeader icon={<User size={13} />} title={t.profileName} />
          <input
            data-testid="settings-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your display name"
            className={inputCls}
          />
          <SectionHeader icon={<Gauge size={13} />} title={t.speedLimit} />
          <div className="flex items-center gap-4">
            <input
              data-testid="settings-speed"
              type="range" min="40" max="140" step="5"
              value={speed}
              onChange={(e) => setSpeed(Number(e.target.value))}
              className="flex-1 accent-brand-600 h-1.5 rounded-full"
            />
            <span className="font-mono font-black text-brand-600 text-[18px] w-20 text-right shrink-0 tabular-nums">
              {speed} <span className="text-[11px] font-bold text-ink-muted">km/h</span>
            </span>
          </div>
          <div className="flex items-center gap-2 pt-1">
            <button
              data-testid="settings-save"
              onClick={saveProfile}
              className="flex-1 py-3 rounded-xl bg-gradient-to-r from-brand-600 to-brand-500 text-white text-[14px] font-bold hover:opacity-95 active:scale-[0.99] transition-all shadow-brand cursor-pointer shimmer-btn"
            >
              {t.save}
            </button>
            {savedMsg && (
              <span data-testid="settings-saved" className="flex items-center gap-1.5 px-3 text-moving font-semibold text-[13px] whitespace-nowrap">
                <CheckCircle2 size={14} /> {savedMsg}
              </span>
            )}
          </div>
        </div>

        {/* Telegram card */}
        <div className="bg-white border border-surface-border rounded-2xl p-5 space-y-3">
          <SectionHeader icon={<Send size={13} />} title="Telegram Alerts" />
          <input
            data-testid="settings-telegram-chatid"
            value={chatId}
            onChange={(e) => setChatId(e.target.value.replace(/[^\d-]/g, ""))}
            placeholder="e.g. 443773823"
            className={`${inputCls} font-mono`}
          />
          <p className="text-[12px] text-ink-soft leading-relaxed">
            Open{" "}
            <a
              href="https://t.me/CarTrackerAlertsBot"
              target="_blank" rel="noreferrer"
              className="font-bold text-brand-600"
              data-testid="settings-tg-bot-link"
            >
              @CarTrackerAlertsBot
            </a>{" "}
            on Telegram, tap <b>Start</b> then send <code className="font-mono bg-surface-muted px-1.5 py-0.5 rounded-md text-ink text-[11px]">/chatid</code> to get your Chat ID.
          </p>
          <button
            type="button"
            data-testid="settings-tg-test"
            onClick={sendTelegramTest}
            disabled={tgSending || !chatId}
            className="w-full py-2.5 rounded-xl border border-brand-200 text-brand-600 font-bold text-[13px] disabled:opacity-50 inline-flex items-center justify-center gap-1.5 hover:bg-brand-50 transition-colors cursor-pointer"
          >
            <Send size={13} /> {tgSending ? "Sending…" : "Send test notification"}
          </button>
          {tgResult && (
            <div
              data-testid="settings-tg-result"
              className={`rounded-xl px-3.5 py-2.5 text-[12px] font-semibold inline-flex items-center gap-2 w-full ${
                tgResult.status === "sent"
                  ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
                  : "bg-amber-50 text-amber-700 border border-amber-100"
              }`}
            >
              {tgResult.status === "sent" ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}
              {tgResult.status === "sent"
                ? "Test message delivered to your Telegram!"
                : `Could not deliver: ${JSON.stringify(tgResult.provider_response || {}).slice(0, 120)}`
              }
            </div>
          )}
          {tgErr && (
            <div data-testid="settings-tg-error" className="text-[12px] text-red-700 bg-red-50 border border-red-100 rounded-xl px-3.5 py-2.5 flex items-center gap-2">
              <AlertTriangle size={13} className="shrink-0" /> {tgErr}
            </div>
          )}
        </div>

        {/* Language card */}
        <div className="bg-white border border-surface-border rounded-2xl p-5 space-y-3">
          <SectionHeader icon={<Languages size={13} />} title={t.language} />
          <div className="flex gap-2">
            <LangBtn testid="lang-en" active={language === "en"} onClick={() => changeLanguage("en")}>
              {t.english}
            </LangBtn>
            <LangBtn testid="lang-hi" active={language === "hi"} onClick={() => changeLanguage("hi")}>
              {t.hindi}
            </LangBtn>
          </div>
        </div>

        {/* Vehicle & links card */}
        <div className="bg-white border border-surface-border rounded-2xl overflow-hidden">
          <LinkRow
            testid="settings-linkdevice"
            onClick={() => nav("/link")}
            icon={<Link2 size={17} />}
            iconBg="bg-brand-50 text-brand-600 border-brand-100"
            title={t.linkDevice}
            subtitle="Pair another car to your account"
          />
          <div className="h-px bg-surface-muted mx-4" />
          <LinkRow
            testid="settings-geofences"
            onClick={() => nav("/geofences")}
            icon={<Smartphone size={17} />}
            iconBg="bg-emerald-50 text-emerald-600 border-emerald-100"
            title={t.geofences}
            subtitle={t.safeZones}
          />
          <div className="h-px bg-surface-muted mx-4" />
          <LinkRow
            testid="settings-headunit"
            onClick={() => nav("/headunit")}
            icon={<Smartphone size={17} />}
            iconBg="bg-purple-50 text-purple-600 border-purple-100"
            title={t.headUnit}
            subtitle="Open simulated car screen"
          />
        </div>

        {/* APK download */}
        <div className="bg-white border border-surface-border rounded-2xl overflow-hidden">
          <a href="/app-debug.apk" download="cartrack-headunit.apk" className="block">
            <LinkRow
              testid="settings-download-apk"
              onClick={() => {}}
              icon={<DownloadCloud size={17} />}
              iconBg="bg-sky-50 text-sky-600 border-sky-100"
              title="Download Car Head APK"
              subtitle="Android app for your head unit"
            />
          </a>
          <div className="px-5 pb-4">
            <p className="text-[12px] text-ink-soft leading-relaxed">
              If your head unit lacks Play Store, install via USB or Bluetooth.
            </p>
            <p className="text-[12px] font-bold text-red-500 mt-1">
              Telegram account required to receive CarTrack alerts.
            </p>
          </div>
        </div>

        {/* Emergency contacts */}
        <EmergencyContacts />

        {/* Account actions */}
        <div className="bg-white border border-surface-border rounded-2xl overflow-hidden">
          <button
            data-testid="settings-logout"
            onClick={onLogout}
            className="w-full flex items-center gap-3 px-5 py-4 hover:bg-surface-soft transition-colors text-left"
          >
            <span className="w-9 h-9 rounded-xl bg-slate-50 border border-surface-border text-ink-soft flex items-center justify-center shrink-0">
              <LogOut size={16} />
            </span>
            <span className="font-semibold text-[14px] text-ink">{t.logout}</span>
          </button>
        </div>

        {/* Danger zone */}
        <div className="bg-red-50 border border-red-100 rounded-2xl overflow-hidden">
          <button
            data-testid="settings-delete-account"
            onClick={onDeleteAccount}
            className="w-full flex items-center gap-3 px-5 py-4 hover:bg-red-100/50 transition-colors text-left"
          >
            <span className="w-9 h-9 rounded-xl bg-red-100 border border-red-200 text-red-600 flex items-center justify-center shrink-0">
              <Trash2 size={16} />
            </span>
            <div>
              <span className="font-bold text-[14px] text-red-600 block">Delete Account</span>
              <span className="text-[11px] text-red-400">Permanently erase all data</span>
            </div>
          </button>
        </div>

        <div className="text-center text-[11px] text-ink-muted pt-1 pb-2 flex items-center justify-center gap-1.5 font-body">
          <Info size={11} /> {t.version} 1.0.1
        </div>

      </div>
    </div>
  );
}

function SectionHeader({ icon, title }) {
  return (
    <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-ink-muted">
      {icon} {title}
    </div>
  );
}

function LangBtn({ active, onClick, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`flex-1 py-2.5 rounded-xl text-[13px] font-bold border transition-all ${
        active
          ? "bg-brand-600 text-white border-brand-600 shadow-brand"
          : "bg-white text-ink-soft border-surface-border hover:border-ink-subtle"
      }`}
    >
      {children}
    </button>
  );
}

function LinkRow({ icon, title, subtitle, onClick, testid, iconBg = "bg-brand-50 text-brand-600 border-brand-100" }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className="w-full flex items-center gap-3 px-5 py-4 hover:bg-surface-soft transition-colors text-left cursor-pointer"
    >
      <span className={`w-9 h-9 rounded-xl border flex items-center justify-center shrink-0 ${iconBg}`}>
        {icon}
      </span>
      <span className="flex-1 min-w-0">
        <span className="block font-semibold text-[14px] text-ink">{title}</span>
        <span className="block text-[12px] text-ink-muted truncate">{subtitle}</span>
      </span>
      <span className="text-ink-subtle text-[14px] shrink-0">›</span>
    </button>
  );
}
